/**
 * ThemeContext - Tema claro/oscuro/automatico para la Comanda App (NER-1282)
 *
 * Caracteristicas:
 * - Preferencia ternaria: 'light' | 'dark' | 'auto' (default 'auto').
 * - `preference` = lo que eligio el usuario; `resolvedTheme` = light/dark final.
 * - En modo 'auto' se suscribe a prefers-color-scheme y reacciona en vivo.
 * - Fallback a 'dark' cuando el SO no responde (kiosks de cocina viejos).
 * - Persiste SOLO la preferencia (no el tema resuelto), prefijada `comanda_`.
 * - Migra la clave legacy binaria `comanda-theme`.
 * - Aplica AMBAS clases en <html>: 'dark' o 'light' (contrato de la comanda app).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';
import {
  PREFERENCE_KEY,
  LEGACY_KEY,
  resolveTheme,
  nextPreference,
  isValidPreference,
  resolveInitialPreference,
  getSystemPrefersDark,
  applyTheme,
} from '../utils/theme';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  // Preferencia inicial: clave nueva > migracion legacy > default 'auto'.
  const [preference, setPreferenceState] = useState(() => {
    const { preference: initial } = resolveInitialPreference(
      storage.get(PREFERENCE_KEY),
      storage.get(LEGACY_KEY)
    );
    return initial;
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  const resolvedTheme = resolveTheme(preference, systemPrefersDark);

  // Limpieza one-time de la clave legacy (la migracion del valor ya ocurrio
  // en el initializer; la persistencia de la clave nueva la hace el effect de abajo).
  useEffect(() => {
    if (storage.get(LEGACY_KEY)) {
      storage.remove(LEGACY_KEY);
    }
  }, []);

  // Aplicar tema resuelto al documento — AMBAS clases (contrato comanda).
  useEffect(() => {
    applyTheme(document.documentElement, resolvedTheme);
  }, [resolvedTheme]);

  // Persistir SOLO la preferencia.
  useEffect(() => {
    storage.set(PREFERENCE_KEY, preference);
  }, [preference]);

  // Suscribirse a prefers-color-scheme SOLO cuando la preferencia es 'auto'.
  useEffect(() => {
    if (preference !== 'auto') return undefined;
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemPrefersDark(e.matches);

    // Sincronizar por si el sistema cambio antes de suscribirnos.
    setSystemPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (isValidPreference(next)) {
      setPreferenceState(next);
    }
  }, []);

  const cyclePreference = useCallback(() => {
    setPreferenceState((prev) => nextPreference(prev));
  }, []);

  const isDark = resolvedTheme === 'dark';

  const value = {
    preference,
    resolvedTheme,
    setPreference,
    cyclePreference,
    // Aliases backward-compatible (consumidor previo: Header).
    theme: preference,
    setTheme: setPreference,
    toggleTheme: cyclePreference,
    isDark,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
